import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { Row } from "@components/Flex";
import SizedBox from "@components/SizedBox";
import SwitchButtons from "@components/SwitchButtons";
import { OPERATIONS_TYPE } from "@src/constants";
import { useStores } from "@stores";
import Skeleton from "react-loading-skeleton";
import Dialog from "@components/Dialog";
import {
  MarketModalsVMProvider,
  useMarketModalVm
} from "@screens/MarketPage/MarketModals/MarketModalVM";
import MarketModalBody from "@screens/MarketPage/MarketModals/MarketModalBody";
import Text from "@components/Text";

interface IProps {}

interface IPropsVM {
  operationName: OPERATIONS_TYPE;
}

const TabsWrapper = styled(Row)`
  border-radius: 16px 16px 0 0;
  height: 56px;
  margin-top: -56px;
`;

const DashboardModalContent: React.FC<IProps> = () => {
  const vm = useMarketModalVm();
  const navigate = useNavigate();
  const [getModalTitles, setModalTitles] = useState<[string, string]>(["", ""]);
  const [isOpen, setOpen] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      document.documentElement.classList.add('modal-open');
      document.body.classList.add('modal-open');
    } else {
      document.documentElement.classList.remove('modal-open');
      document.body.classList.remove('modal-open');
    }
    
    return () => {
      document.documentElement.classList.remove('modal-open');
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  useMemo(() => {
    const supplyTitles: [string, string] = ["Supply", "Withdraw"];
    const borrowTitles: [string, string] = ["Borrow", "Repay"];
    if (
      [OPERATIONS_TYPE.SUPPLY, OPERATIONS_TYPE.WITHDRAW].includes(
        vm.operationName
      )
    )
      setModalTitles(supplyTitles);
    else setModalTitles(borrowTitles);

    if (
      [OPERATIONS_TYPE.WITHDRAW, OPERATIONS_TYPE.REPAY].includes(
        vm.operationName
      )
    )
      vm.setMarketModalStep(1);
    else vm.setMarketModalStep(0);
    setOpen(true);
  }, [vm]);

  const setActiveTab = (step: 0 | 1) => {
    if (
      [OPERATIONS_TYPE.SUPPLY, OPERATIONS_TYPE.WITHDRAW].includes(
        vm.operationName
      )
    ) {
      const operation =
        vm.operationName === OPERATIONS_TYPE.SUPPLY
          ? OPERATIONS_TYPE.WITHDRAW
          : OPERATIONS_TYPE.SUPPLY;
      vm.setMarketModalStep(step);
      return navigate(
        `/${vm.urlParams?.marketId}/${operation}/${vm.urlParams?.tokenId}`
      );
    }

    if (
      [OPERATIONS_TYPE.BORROW, OPERATIONS_TYPE.REPAY].includes(vm.operationName)
    ) {
      const operation =
        vm.operationName === OPERATIONS_TYPE.BORROW
          ? OPERATIONS_TYPE.REPAY
          : OPERATIONS_TYPE.BORROW;
      vm.setMarketModalStep(step);
      return navigate(
        `/${vm.urlParams?.marketId}/${operation}/${vm.urlParams?.tokenId}`
      );
    }
  };

  const closeTab = () => {
    setOpen(false);
    navigate(`/${vm.urlParams?.marketId}`);
  };

  return (
    <Dialog
      classNames={{ wrapper: "dashboard-dialog" }}
      title="Operations"
      visible={isOpen}
      onClose={closeTab}
      style={{ maxWidth: "415px" }}
    >
      <SizedBox height={72} />
      <TabsWrapper>
        <SwitchButtons
          values={getModalTitles}
          active={vm.marketModalStep}
          onActivate={(v: 0 | 1) => setActiveTab(v)}
          border
        />
      </TabsWrapper>
      {vm.token ? (
        <MarketModalBody
          urlParams={vm.urlParams}
          operationName={vm.operationName}
          tokenStats={vm.token}
          onClose={closeTab}
        />
      ) : (
        <div style={{ padding: "24px", textAlign: "center" }}>
          <Text size="medium" type="secondary">
            Unable to load token data. Please try refreshing the page.
          </Text>
        </div>
      )}
    </Dialog>
  );
};

const MarketModal: React.FC<IPropsVM> = ({ operationName }) => {
  console.log("MarketModal");
  const urlParams = useParams<{ tokenId: string; marketId: string }>();
  const { lendStore } = useStores();
  const [isOpen, setOpen] = useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      document.documentElement.classList.add('modal-open');
      document.body.classList.add('modal-open');
    } else {
      document.documentElement.classList.remove('modal-open');
      document.body.classList.remove('modal-open');
    }
    
    return () => {
      document.documentElement.classList.remove('modal-open');
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  const handleClose = () => {
    setOpen(false);
    navigate(`/${urlParams?.marketId}`);
  };

  return (
    <MarketModalsVMProvider operationName={operationName} urlParams={urlParams}>
      {lendStore.initialized ? (
        <DashboardModalContent />
      ) : (
        <Dialog
          classNames={{ wrapper: "dashboard-dialog" }}
          title="Operations"
          visible={isOpen}
          onClose={handleClose}
          style={{ maxWidth: "415px" }}
        >
          <Skeleton height={56} style={{ marginBottom: 8 }} count={4} />
        </Dialog>
      )}
    </MarketModalsVMProvider>
  );
};

export default observer(MarketModal);
